const COOKIE_NAME = 'pblk_sid';
const TTL_MINUTES = 30;
const EXTERNAL_COOKIE_WAIT_MS = 50;
const POLL_INTERVAL_MS = 5;

// leadclump-flow's hash parser validates l_s against this exact regex. Any pblk_sid
// value we adopt must satisfy it, otherwise downstream correlation breaks silently.
const SID_VALIDATION_REGEX = /^[A-Za-z0-9-]{8,64}$/;

export type SessionIdSource = 'cookie' | 'generated';

let cachedSessionId: string | null = null;
// 'cookie' means we adopted an externally-written pblk_sid (leadclump-flow's prep on the
// publisher page). 'generated' means we minted our own UUID because no cookie was present
// in time. Used in canary monitoring to detect when the load-order handshake fails.
let sessionIdSource: SessionIdSource = 'generated';

function getCookie(name: string): string {
  try {
    const row = document.cookie
      .split('; ')
      .find((r) => r.startsWith(name + '='));
    if (!row) return '';
    // Use indexOf instead of split('=')[1] so values containing '=' (e.g. base64
    // padding from a future writer) are not truncated.
    const value = row.slice(row.indexOf('=') + 1);
    return value ? decodeURIComponent(value) : '';
  } catch {
    return '';
  }
}

function hasValidSessionCookie(): boolean {
  const value = getCookie(COOKIE_NAME);
  // Empty-value cookies (e.g. `pblk_sid=` written by a half-completed prep script)
  // would falsely signal "external writer ready" and collapse the wait window to 0ms.
  // Validate against leadclump-flow's regex so a malformed value also defers to our
  // generated SID rather than poisoning the redirect hash.
  return value !== '' && SID_VALIDATION_REGEX.test(value);
}

function setCookie(name: string, value: string, minutes: number): void {
  try {
    const date = new Date();
    date.setTime(date.getTime() + minutes * 60 * 1000);
    // Cross-site embed support: SameSite=None; Secure works inside iframes on HTTPS.
    // Browsers reject SameSite=None without Secure, so on http (localhost dev) fall back to Lax.
    const isSecure = typeof location !== 'undefined' && location.protocol === 'https:';
    const sameSite = isSecure ? 'None; Secure' : 'Lax';
    document.cookie =
      `${name}=${encodeURIComponent(value)}; ` +
      `expires=${date.toUTCString()}; ` +
      `path=/; SameSite=${sameSite}`;
  } catch {
    // strict privacy / blocked cookies — fall through; getSessionId still returns the in-memory value
  }
}

function generateSessionId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // Must match leadclump-flow's hash-parser regex /^[A-Za-z0-9-]{8,64}$/ — no dots,
    // no underscores. performance.now() is unsafe here because it returns a float.
    const rand = () => Math.random().toString(16).slice(2, 10).padStart(8, '0');
    return `${rand()}-${rand()}-${rand()}-${rand()}`;
  }
}

/**
 * Give an external script (leadclump-flow on the publisher page) a brief window to
 * write the pblk_sid cookie before the first getSessionId() call locks our cache.
 * Resolves immediately if a *valid* cookie is already present, or after maxMs otherwise.
 *
 * Uses performance.now() because Date.now() can jump backward/forward on NTP correction
 * or mobile sleep/wake, which would make the polling loop spin or exit early.
 */
export async function waitForExternalSessionCookie(maxMs = EXTERNAL_COOKIE_WAIT_MS): Promise<void> {
  const start = performance.now();
  while (!hasValidSessionCookie() && performance.now() - start < maxMs) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
}

export function getSessionId(): string {
  if (cachedSessionId) {
    // Sliding TTL: refresh expiry on every read so an active user stays in one session.
    // Aligns with GA4's 30-min idle timeout semantic.
    setCookie(COOKIE_NAME, cachedSessionId, TTL_MINUTES);
    return cachedSessionId;
  }
  const cookieValue = getCookie(COOKIE_NAME);
  // Only adopt a cookie value if it matches the leadclump-flow contract. A malformed
  // value (truncated, dotted, encoded oddly) would break landing-side correlation, so
  // we treat it as "no cookie" and mint our own.
  let sessionId: string;
  if (cookieValue && SID_VALIDATION_REGEX.test(cookieValue)) {
    sessionId = cookieValue;
    sessionIdSource = 'cookie';
  } else {
    sessionId = generateSessionId();
    sessionIdSource = 'generated';
    if (import.meta.env.DEV) {
      console.warn(
        '[leadclump-quiz] pblk_sid cookie not present (or invalid) at first getSessionId(); generated new SID. leadclump-flow correlation may diverge.'
      );
    }
  }
  setCookie(COOKIE_NAME, sessionId, TTL_MINUTES);
  cachedSessionId = sessionId;
  return sessionId;
}

export function getSessionIdSource(): SessionIdSource {
  return sessionIdSource;
}

export function __resetSessionForTests(): void {
  cachedSessionId = null;
  sessionIdSource = 'generated';
}
